<script setup lang="ts">
// Vue & Plugins
import { ref, computed, Ref, useTemplateRef } from 'vue'
import {
  refDebounced,
  useInfiniteScroll,
  useMutationObserver,
} from '@vueuse/core'

// Composables
import { useAreaQueryInfinite } from '../../composables/useArea'

// Models
import { SiteID } from '../../../sites/models/site'
import type { Area } from '../../models/area'
import { groupItems } from '../../../common/utils/groupingUtils'
import {
  ResourceKeys,
  useResourceNames,
} from '../../../common/composables/resource-names'
import { useI18n } from 'vue-i18n'
import {
  buildingDisplayName,
  BuildingID,
} from '../../../buildings/models/building'

const { t } = useI18n({ inheritLocale: true })
const { getSingular } = useResourceNames()

const selected = defineModel<Area | null | undefined>('selected', {
  required: true,
})

const {
  siteId,
  buildingId = undefined,
  disabled = false,
} = defineProps<{
  siteId: SiteID
  buildingId?: BuildingID
  disabled?: boolean
}>()

const nameInput = ref('')
const name = refDebounced(nameInput, 500)

const isShown: Ref<boolean> = ref(false)

const { data, isFetching, fetchNextPage, hasNextPage } = useAreaQueryInfinite(
  siteId,
  { name, buildingId: () => buildingId },
  { enabled: isShown },
)

const areas = computed<Area[]>(
  () =>
    data?.value?.pages.reduce((acc, curr) => {
      return acc.concat(curr.areas)
    }, [] as Area[]) ?? [],
)

const optionsCombined = computed(() =>
  !selected.value ? areas.value : [selected.value, ...areas.value],
)

const options = computed(() =>
  Array.from(
    new Map(optionsCombined.value.map((item) => [item.id, item])).values(),
  ),
)

const groupedOptions = computed(() =>
  groupItems(
    options.value,
    (area) =>
      area.building
        ? buildingDisplayName(area.building)
        : content.value.optionGroupLabelDefault,
    (area) =>
      area.building?.customFields?.business_unit
        ? area.building.customFields.business_unit
        : undefined,
  ),
)

const content = computed(() => {
  return {
    placeholder: t('resources.inputs.select.placeholder', {
      resource: getSingular(ResourceKeys.AREA).value,
    }),

    optionGroupLabelDefault: t(
      'resources.inputs.select.option_group_label_default',
      { resource: getSingular(ResourceKeys.BUILDING).value },
    ),
  }
})

const areaSelect = useTemplateRef<HTMLElement>('area-select')
const areaSelectContainer: Ref<HTMLElement | null> = ref(null)
useInfiniteScroll(
  areaSelectContainer,
  () => {
    if (isFetching.value) {
      return
    }

    fetchNextPage()
  },
  {
    canLoadMore: () => hasNextPage.value,
  },
)

useMutationObserver(
  areaSelect,
  () => {
    const selectListContainer = document.querySelector(
      '.p-select-list-container',
    )
    if (selectListContainer) {
      areaSelectContainer.value = selectListContainer as HTMLElement
    }
  },
  {
    childList: true,
    subtree: true,
  },
)
</script>

<template>
  <Select
    ref="area-select"
    v-model="selected"
    :options="groupedOptions"
    :loading="isFetching"
    filter
    :filter-fields="[
      'name',
      'building.name',
      'building.customFields.business_unit',
    ]"
    option-group-label="label"
    option-group-children="items"
    option-label="name"
    :placeholder="content.placeholder"
    pt:option-label:class="pl-6"
    pt:option-group-label:class="text-color-emphasis"
    data-key="id"
    :disabled="disabled"
    @filter="nameInput = $event.value"
    @show="isShown = true"
    @hide="isShown = false"
  >
    <template #optiongroup="{ option }">
      <span class="whitespace-nowrap text-color-emphasis">
        <Tag v-if="option.tag" :value="option.tag" class="mr-1" />
        {{ option.label }}
      </span>
    </template>
  </Select>
</template>
